import { useState, useRef, useEffect, useMemo } from "react";
import { initializeAccountPortalState } from "../state/reducers.js";
import "../state/actions.js";
import { getAccountPortalState } from "../state/utils.js";
import { getReduxStore } from "../store/state/redux-host.js";
import "../store/state/host.js";
import "../store/state/modal.js";
import "../store/storage/memory-storage.js";
import "mitt";
import "../store/storage/universal-memory-storage.js";
import { getRuntimeInfo, getAuthentication } from "./state-getters.js";
import { getProfileData } from "./state-triggers.js";
function useAccountPortalState() {
  const [state, setState] = useState(getAccountPortalState());
  const isMounted = useRef(false);
  function handleStateUpdated() {
    if (!isMounted.current) {
      return;
    }
    const currState = getAccountPortalState();
    if (currState !== state) {
      setState(currState);
    }
  }
  useEffect(() => {
    initializeAccountPortalState();
    isMounted.current = true;
    const unsubscribe = getReduxStore().subscribe(handleStateUpdated);
    return () => {
      isMounted.current = false;
      unsubscribe();
    };
  }, []);
  return state || {};
}
function useBasicUser() {
  const auth = useAuthentication();
  return useMemo(() => auth.user, [auth.user]);
}
function useLightUser() {
  const apState = useAccountPortalState();
  const isInitial = useRef(true);
  const { lightUser, error } = useMemo(() => {
    if (isInitial.current) {
      isInitial.current = false;
      return {};
    } else {
      return apState;
    }
  }, [apState.lightUser, apState.error]);
  return {
    lightUser,
    error
  };
}
function useProfileData() {
  const { profileData, isAuthenticated } = useAccountPortalState();
  useEffect(() => {
    if (isAuthenticated && !profileData) {
      getProfileData();
    }
  }, [isAuthenticated]);
  return profileData;
}
function useRuntimeInfo() {
  const {
    isModalVisible,
    pageContext,
    preset,
    touchpoint,
    clientVersion,
    mfId,
    hasModalClient,
    isAccountPortalEnabled,
    isAuthProvider,
    propSourceId,
    hintAcid
  } = useAccountPortalState();
  return useMemo(getRuntimeInfo, [
    isModalVisible,
    pageContext,
    preset,
    touchpoint,
    clientVersion,
    mfId,
    hasModalClient,
    isAccountPortalEnabled,
    isAuthProvider,
    propSourceId,
    hintAcid
  ]);
}
function useAuthentication() {
  const { isAuthenticated, authContext, user, error } = useAccountPortalState();
  return useMemo(getAuthentication, [isAuthenticated, authContext, user, error]);
}
function useRegistrationStatus() {
  return useEventStatus("registration");
}
function useLightRegistrationStatus() {
  return useEventStatus("lightRegistration");
}
function useLoginStatus() {
  return useEventStatus("login");
}
function useLogoutStatus() {
  return useEventStatus("logout");
}
function useSessionReloadStatus() {
  return useEventStatus("sessionReload");
}
function useGetProfileDataStatus() {
  return useEventStatus("getProfileData");
}
function usePasswordCreateStatus() {
  return useEventStatus("createPassword");
}
function usePasswordResetStartStatus() {
  return useEventStatus("passwordResetStart");
}
function usePasswordResetCompleteStatus() {
  return useEventStatus("passwordResetComplete");
}
function useExternalAuthStatus() {
  return useEventStatus("externalAuth");
}
function useEventStatus(event) {
  const isFirstRender = useRef(true);
  const { eventType, status, error } = useAccountPortalState();
  const [eventStatus, setEventStatus] = useState({
    isPending: false,
    isSuccess: false,
    error: null
  });
  function updateEventStatus(status2) {
    setEventStatus({
      isPending: false,
      isSuccess: false,
      error: null,
      ...status2
    });
  }
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (eventType !== event) {
      return;
    }
    if (status === "pending") {
      return updateEventStatus({ isPending: true });
    }
    if (status === "success") {
      return updateEventStatus({ isSuccess: true });
    }
    if (status === "error") {
      return updateEventStatus({ error });
    }
  }, [eventType, status, error]);
  return eventStatus;
}
export {
  useAccountPortalState,
  useAuthentication,
  useBasicUser,
  useExternalAuthStatus,
  useGetProfileDataStatus,
  useLightRegistrationStatus,
  useLightUser,
  useLoginStatus,
  useLogoutStatus,
  usePasswordCreateStatus,
  usePasswordResetCompleteStatus,
  usePasswordResetStartStatus,
  useProfileData,
  useRegistrationStatus,
  useRuntimeInfo,
  useSessionReloadStatus
};
//# sourceMappingURL=hooks.js.map
